import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import CustomPagination from "../CustomPagination/CustomPagination";
import "../PRmanagerLayout/PRmanager.css";
import "./SubAccount.css";
import { useState, useEffect, useMemo, useRef } from "react";
import Instance from "../../Utils/Axios";
import { Link, useNavigate } from "react-router-dom";
import { showToast } from "../../Utils/index";
import PremiumLoader from "../Loader/PremiumLoader";
import useKeyScroll from "../../Hooks/useKeyScroll";

const TruncatedCell = ({ text, id, maxLines = 2 }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollHeight > textRef.current.clientHeight
        );
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [text]);

  const content = (
    <div
      ref={textRef}
      className="w-100 m-0 p-0"
      style={{
        whiteSpace: "normal",
        wordBreak: "break-word",
        display: "-webkit-box",
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        cursor: isTruncated ? "pointer" : "default",
      }}
    >
      {text}
    </div>
  );

  return isTruncated ? (
    <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{text}</Tooltip>}>
      {content}
    </OverlayTrigger>
  ) : (
    content
  );
};

export default function SubLayout() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [filterQuery, setfilterQuery] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();
  const scrollContainerRef = useKeyScroll();

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString.replace(" ", "T"));
    if (isNaN(date.getTime())) return dateString;

    const optionsDate = { month: 'short', day: 'numeric', year: 'numeric' };
    const optionsTime = { hour: 'numeric', minute: '2-digit', hour12: true };

    const formattedDate = date.toLocaleDateString('en-US', optionsDate);
    const formattedTime = date.toLocaleTimeString('en-US', optionsTime);

    return `${formattedDate} • ${formattedTime}`;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setfilterQuery(value);
    if (!value) {
      setSearchKeyword("");
      setCurrentPage(1);
    }
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1);
    setSearchKeyword(filterQuery);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  useEffect(() => {
    const fetchCompanies = () => {
      setListLoading(true);
      const token = localStorage.getItem("tokenId");
      const payload = {
        status: "",
        pageNumber: currentPage,
        pageSize: rowsPerPage,
      };

      if (sortConfig.key) {
        payload.orderBy = sortConfig.key;
        payload.orderByType = sortConfig.direction === "ascending" ? "ASC" : "DESC";
      }

      if (searchKeyword) {
        payload.filterQuery = searchKeyword;
      }

      Instance.post("/user/usercompany/getList", payload)
        .then((response) => {
          if (response.data.resCode === "200") {
            setCompanies(response.data.message || []);
            setTotalPages(response.data.totalPages || 1);
            setTotalRecords(response.data.totalRecord || 0);
            setError("");
          } else {
            setError("Failed to fetch companies");
            console.error(
              "Error fetching companies data:",
              response.data.resMessage
            );
          }
        })
        .catch((error) => {
          setError("Failed to fetch companies");
          console.error("Error fetching companies:", error);
        })
        .finally(() => {
          setListLoading(false);
          setLoading(false);
        });
    };

    fetchCompanies();
  }, [currentPage, rowsPerPage, sortConfig, searchKeyword]);

  const handleShowDelete = (companyId) => {
    setSelectedCompanyId(companyId);
    setShowDelete(true);
  };

  const handleNotShowDelete = () => {
    setShowDelete(false);
    setSelectedCompanyId(null);
  };


  const handleDelete = async (companyId) => {
    try {
      const token = localStorage.getItem("tokenId");
      const response = await Instance.post(`/user/usercompany/update`, {
        // tokenId: token,
        companyId: companyId,
        status: "INACTIVE",
      });
      if (response.data.resCode === "200") {
        showToast("Company Deleted successfully!", "success", "succes");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setError(response.data.resMessage);
      }
    } catch (error) {
      console.error("Error deleting the company details:", error);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const currentSortedCompanies = companies;

  const renderSortIcon = (field) => {
    if (sortConfig.key !== field) {
      return (
        <FontAwesomeIcon
          icon={faArrowDown}
          style={{ marginLeft: 6, opacity: 0.3 }}
        />
      );
    }

    return (
      <FontAwesomeIcon
        icon={sortConfig.direction === "ascending" ? faArrowUp : faArrowDown}
        style={{ marginLeft: 6, opacity: 1 }}
      />
    );
  };

  const handleSortChange = (selectedColumn) => {
    setSortConfig((prev) => ({
      key: selectedColumn.sortField,
      direction:
        prev.key === selectedColumn.sortField &&
        prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            COMPANY NAME {renderSortIcon("name")}
          </div>
        ),
        selector: (row) => row.name,
        sortable: true,
        sortField: "name",
        width: "250px",
        cell: (row, index) => (
          <TruncatedCell
            text={row.name || "N/A"}
            id={`tooltip-table-company-${index}`}
            maxLines={2}
          />
        ),
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            CONTACT PERSON {renderSortIcon("contactPerson")}
          </div>
        ),
        selector: (row) => row.contactPerson,
        sortable: true,
        sortField: "contactPerson",
        width: "180px",
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            PHONE NUMBER {renderSortIcon("mobile")}
          </div>
        ),
        selector: (row) => row.mobile,
        sortable: true,
        sortField: "mobile",
        width: "180px",
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            EMAIL {renderSortIcon("email")}
          </div>
        ),
        selector: (row) => row.email,
        sortable: true,
        sortField: "email",
        width: "200px",
        cell: (row, index) => (
          <TruncatedCell
            text={row.email || "N/A"}
            id={`tooltip-table-email-${index}`}
            maxLines={2}
          />
        ),
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            CREATED ON {renderSortIcon("createdOn")}
          </div>
        ),
        selector: (row) => row.createdOn,
        sortable: true,
        sortField: "createdOn",
        width: "220px",
        cell: (row) => (
          <span className="created_on_col">{formatDate(row.createdOn)}</span>
        ),
      },
      {
        name: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            ACTIONS
          </div>
        ),
        center: true,
        width: "130px",
        style: {
          justifyContent: "center",
        },
        headerStyle: {
          justifyContent: "center",
        },
        cell: (row) => (
          <div className="sub-account-actions-cell">
            <img
              src="/build/edit.svg"
              alt="edit"
              width={24}
              height={24}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCompanyId(row.companyId);
                setShowEditModal(true);
              }}
            />
            <img
              src="/build/delete.svg"
              alt="delete"
              width={24}
              height={24}
              onClick={(e) => {
                e.stopPropagation();
                handleShowDelete(row.companyId);
              }}
            />
          </div>
        ),
      },
      {
        name: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            STATUS
          </div>
        ),
        center: true,
        width: "120px",
        cell: (row) => (
          <div
            className={
              row.status === "ACTIVE" ? "sub_enabled mx-auto" : "sub_disabled mx-auto"
            }
          >
            {row.status}
          </div>
        ),
      },
    ],
    [sortConfig]
  );

  const customStyles = {
    table: {
      style: {
        minWidth: "max-content",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        minHeight: "52px",
        minWidth: "max-content",
      },
    },
    headCells: {
      style: {
        fontSize: "13px",
        textTransform: "uppercase",
        fontWeight: "700",
        color: "#444444",
        flexShrink: 0,
        borderRight: "none",
      },
    },
    cells: {
      style: {
        color: "#6c757d",
        fontSize: "14px",
        fontWeight: "500",
        whiteSpace: "normal",
        flexShrink: 0,
        borderRight: "none",
      },
    },
    rows: {
      style: {
        minHeight: "70px",
        minWidth: "max-content",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#f5f6ff",
        outline: "none",
        transition: "background-color 0.2s ease",
      },
    },
  };

  return (
    <>

      <div className="sub_layout">
        <div className={`pr_secondary_nav d-flex align-items-center px-3 justify-content-between`}>
          <div className="search-bar" style={{ zIndex: 10, position: 'relative' }}>
            <input
              type="search"
              value={filterQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search Sub-Account"
            />
            <img
              src="/build/img/search.svg"
              alt="search"
              width={20}
              height={20}
              style={{ cursor: "pointer" }}
              onClick={handleSearchSubmit}
            />
          </div>
          <Link to="/customer/sub-account/add" style={{ zIndex: 10, position: 'relative' }}>
            <button
              className="pr_form_next_btn d-flex align-items-center gap-2 px-4 shadow-sm"
              style={{ width: 'fit-content', height: '40px' }}
            >
              <img
                src="/build/img/Icons/Vector.png"
                alt="add"
                width={12}
                height={12}
              />
              Add New
            </button>
          </Link>
          <img
            className={"pr_group_img"}
            src="/build/img/Group.png"
            alt="group img"
            width={620}
            height={60}

          />
        </div>

        {loading ? (
          <PremiumLoader fullPage={false} text="Fetching sub-accounts..." />
        ) : (
          <>
            <div className="my_pr_tables">
              <div
                className="pr_table_scroll_wrapper"
                ref={scrollContainerRef}
                tabIndex="0"
                style={{ outline: "none" }}
              >
                {listLoading ? (
                  <div className="text-center py-5 d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                    <PremiumLoader fullPage={false} text="Updating list..." />
                  </div>
                ) : currentSortedCompanies.length === 0 ? (
                  <div className="empty-state-container empty-state-package m-4">
                    <div className="empty-state-subaccount">
                      <div className="mb-3">
                        <img
                          src="/build/img/Company.svg"
                          alt="No Sub-account"
                          width="80"
                          height="80"
                          className="opacity-75"
                        />
                      </div>
                      <h4 className="fw-bold mb-2">No sub-accounts created yet.</h4>
                      <p className="text-muted mb-4">
                        Add team members to manage PR campaigns together.
                      </p>
                      <button
                        className="empty-state-btn"
                        onClick={() => navigate("/customer/sub-account/add")}
                      >
                        <img
                          src="/build/img/Icons/Vector.png"
                          alt="add"
                          width="13"
                          height="13"
                        />
                        Add Sub-Account
                      </button>
                    </div>
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={currentSortedCompanies}
                    customStyles={customStyles}
                    sortServer
                    onSort={handleSortChange}
                    sortIcon={<></>}
                    highlightOnHover
                    pointerOnHover
                    dense={false}
                    conditionalRowStyles={[
                      {
                        when: (row) => selectedCompanyId === row.companyId,
                        style: {
                          backgroundColor: "#f5f6ff",
                        },
                      },
                    ]}
                    className="sub-account-table-react"
                  />
                )}
              </div>
              <div className="sub-account-footer-pagination">
                <CustomPagination
                  totalItems={totalRecords}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </div>
            </div>
          </>
        )}
      </div >

      {/* Dynamic Modals moved outside the loop for performance and data integrity */}
      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        className="custom_modal company_modal delete_modal_v2 edit_modal_v2"
      >
        <Modal.Body>
          <div className="delete_modal_icon_wrapper">
            {/* <div className="delete_modal_icon_bg edit_icon_bg">
              <img
                src="/build/edit.svg"
                alt="Edit Icon"
                width={32}
                height={32}
              />
            </div> */}
          </div>
          <div className="delete_modal_text_container mb-1">
            <h5 className="delete_modal_head">
              Edit Sub Account
              {/* (ID: {selectedCompanyId}) */}
            </h5>
            <p className="delete_modal_content">
              Editing this sub-account will update all
              currently published and upcoming PRs with
              the new details. Do you wish to proceed?
            </p>
          </div>
          <div className="delete_modal_actions d-flex justify-content-center gap-3">
            <Button
              className="delete_modal_btn_cancel"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="delete_modal_btn_proceed"
              onClick={() => {
                navigate(
                  `/customer/sub-account/edit/${selectedCompanyId}`
                );
              }}
            >
              Proceed
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete Modal */}
      <Modal
        show={showDelete}
        onHide={handleNotShowDelete}
        centered
        className="custom_modal company_modal delete_modal_v2"
      >
        <Modal.Body>
          <div className="delete_modal_icon_wrapper">
            {/* <div className="delete_modal_icon_bg">
              <img
                src="/build/delete.svg"
                alt="delete icon"
                width={24}
                height={24}
              />
            </div> */}
          </div>
          <div className="delete_modal_text_container mb-1">
            <h5 className="delete_modal_head">
              Delete PR
              {/* (ID: {selectedCompanyId}) */}
            </h5>
            <p className="delete_modal_content">
              Are you sure you want to delete this PR?
            </p>
          </div>
          <div className="delete_modal_actions d-flex justify-content-center gap-3">
            <Button
              className="delete_modal_btn_cancel"
              onClick={handleNotShowDelete}
            >
              Cancel
            </Button>
            <Button
              className="delete_modal_btn_delete"
              onClick={() => {
                handleDelete(selectedCompanyId);
              }}
            >
              {/* <img
                src="/build/delete.svg"
                alt="delete"
                width={14}
                height={14}
                className="me-2 filter-white"
              /> */}
              Delete
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
